import React, {useEffect, useMemo, useState} from "react";
import {Button, DatePicker, Select, Space, Tabs, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {DownloadOutlined, ReloadOutlined} from "@ant-design/icons";
import {LinkIcon} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {AppDispatch, RootState} from "../../redux/store";
import {getPaymentLinkReport, repushPaymentLinkPayment} from "../../redux/slice/paymentLinkSlice";
import {adminDownload, buildQuery, getReturnedObject} from "../../api/adminApi";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../auth/adminPermissions";
import {HeroHeader, Card, SearchField, formatNaira, COLORS} from "../../designSystem";

const {RangePicker} = DatePicker;

// NGN is shown as ₦ (brand rule); any other currency keeps its ISO code.
const money = (value: any, currency = "NGN") =>
  !currency || currency === "NGN" ? formatNaira(value) : `${currency} ${Number(value || 0).toLocaleString()}`;
const moneyCell = (value: any, currency = "NGN") => <span className="font-mono">{money(value, currency)}</span>;

/**
 * Payment statuses, as the API numbers them. Each is its own tab because the endpoint takes a
 * single status — so a tab is one filtered query, not a slice of the page already loaded.
 */
const STATUS_TABS: Array<{key: string; label: string; status?: number}> = [
  {key: "all", label: "All"},
  {key: "paid", label: "Paid", status: 4},
  {key: "pending", label: "Pending", status: 1},
  {key: "awaiting", label: "Awaiting Transfer", status: 2},
  {key: "verifying", label: "Verification Queued", status: 3},
  {key: "failed", label: "Failed", status: 5},
  {key: "expired", label: "Expired", status: 6},
  {key: "cancelled", label: "Cancelled", status: 7},
];

const LINK_TYPES = [
  {label: "General", value: 1},
  {label: "Event Contribution", value: 2},
  {label: "Fundraiser", value: 3},
  {label: "Donation", value: 4},
  {label: "Invoice", value: 5},
];

const statusLabel = (value: any) => {
  const status = Number(value);
  const match = STATUS_TABS.find((tab) => tab.status === status);
  return match ? match.label : String(value || "-");
};

/** Paid is settled, the in-flight states are amber, everything else failed to land. */
const statusColour = (value: any) => {
  const status = Number(value);
  if (status === 4) return "green";
  if (status === 1 || status === 2 || status === 3) return "orange";
  return "red";
};

const apiDate = (value: any) => value?.format?.("YYYY-MM-DD");

function PaymentLinkReports() {
  const dispatch: AppDispatch = useDispatch();
  const {token, permissions, roles} = useSelector((state: RootState) => state.account);
  const {report, reportSummary, pageInfo, loading} = useSelector((state: RootState) => state.paymentLinks);
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("all");
  const [type, setType] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<any>(null);
  const [exporting, setExporting] = useState(false);
  const [repushingId, setRepushingId] = useState<string | null>(null);
  const canRepush = hasAdminPermission(permissions, roles, ADMIN_PERMISSIONS.paymentLinkRepush);

  const status = STATUS_TABS.find((entry) => entry.key === tab)?.status;
  const fromDate = apiDate(dateRange?.[0]);
  const toDate = apiDate(dateRange?.[1]);

  const load = (pageNumber = 1) => {
    if (token) {
      dispatch(getPaymentLinkReport({token, searchParam, pageNumber, status, type, fromDate, toDate}));
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchParam, status, type, fromDate, toDate]);

  const repush = async (row: any) => {
    if (!token || !canRepush || repushingId) return;
    setRepushingId(row.paymentId);
    try {
      const payload = await dispatch(repushPaymentLinkPayment({token, paymentId: row.paymentId})).unwrap();
      const payment = getReturnedObject(payload);
      if (payment?.adminBalanceRefunded) {
        message.info("This payment was already restored to balance. No additional credit applied.");
      } else if (Number(payment?.status) === 4) {
        message.success(payload?.message || "VFD confirmed the payment. The user's balance has been credited.");
      } else {
        message.warning(payment?.lastVerificationError
          ? `${payment.lastVerificationError} No credit applied.`
          : "VFD has not confirmed this payment. No credit applied; verification will retry.");
      }
    } catch (error: any) {
      message.error(error?.message || "Could not repush this payment. Refresh the report before retrying.");
    } finally {
      setRepushingId(null);
      load(pageInfo?.pageNumber || 1);
    }
  };

  const exportReport = async () => {
    if (!token) return;
    setExporting(true);
    try {
      // The export mirrors what is on screen. Downloading every payment while the page shows
      // one filtered tab would quietly hand over the wrong file.
      const blob = await adminDownload(
        `/payment-links/report/export${buildQuery({searchParam, status, type, fromDate, toDate})}`,
        token,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const scope = [tab === "all" ? null : tab, type ? `type-${type}` : null].filter(Boolean).join("-");
      link.download = `payment-link-report${scope ? `-${scope}` : ""}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error.message || "Could not export report");
    } finally {
      setExporting(false);
    }
  };

  const suggestions = useMemo(
    () => Array.from(new Set((report || []).flatMap((row: any) =>
      [row.paymentLinkTitle, row.ownerEmail, row.payerEmail].filter(Boolean)))),
    [report],
  );

  const table = (
    <>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <SearchField
          className="lg:max-w-md lg:flex-1"
          placeholder="Search by link title, owner or payer"
          value={searchParam}
          onChange={(event) => setSearchParam(event.target.value)}
          loading={loading}
          historyKey="payment-link-reports"
          // Type-ahead from what is already on screen: the owners and payers in the loaded
          // page are the terms most likely to be searched next.
          suggestions={suggestions as string[]}
        />

        <Select
          allowClear
          placeholder="Any link type"
          value={type}
          onChange={setType}
          options={LINK_TYPES}
          style={{width: 200, height: 44}}
        />

        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          allowClear
          placeholder={["Paid from", "Paid to"]}
          style={{height: 44}}
        />
      </div>

      <GenericTable
        loading={loading}
        pageInfo={pageInfo}
        data={report}
        handlePageChange={load}
        columns={[
          {title: "Payment Link", dataIndex: "paymentLinkTitle", key: "paymentLinkTitle"},
          {title: "Owner", dataIndex: "ownerEmail", key: "ownerEmail"},
          {title: "Payer", dataIndex: "payerEmail", key: "payerEmail"},
          {title: "Amount", key: "amount", render: (_: any, row: any) => moneyCell(row.amount, row.currency)},
          {title: "Charge", key: "charge", render: (_: any, row: any) => moneyCell(row.chargeAmount, row.currency)},
          {title: "VFD Reference", dataIndex: "vfdReference", key: "vfdReference"},
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: any) => <Tag color={statusColour(value)}>{statusLabel(value)}</Tag>,
          },
          {
            title: "Paid At",
            dataIndex: "paidAt",
            key: "paidAt",
            render: (value: string) => value ? new Date(value).toLocaleString() : "Not paid",
          },
          ...(canRepush ? [{
            title: "Action", key: "action",
            render: (_: any, row: any) => {
              if (Number(row.status) === 4) return <Tag color="green">Credited</Tag>;
              if (row.adminBalanceRefunded) return <Tag color="blue">Balance restored</Tag>;
              if (Number(row.status) === 7) return <span className="text-slate-400">Cancelled</span>;
              return <Button icon={<ReloadOutlined/>} loading={repushingId === row.paymentId}
                disabled={!!repushingId || !row.vfdReference} onClick={() => repush(row)}>Repush</Button>;
            },
          }] : []),
        ]}
      />
    </>
  );

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={LinkIcon}
          title="Payment Link Reports"
          // The summary follows the filters, so it describes what is on screen rather than
          // a total the visible rows cannot account for.
          subtitle={`${money(reportSummary?.totalAmount)} across ${reportSummary?.totalPayments || 0} payments`}
          actions={
            <button
              onClick={exportReport}
              disabled={exporting}
              className="inline-flex items-center gap-2 font-semibold h-12 px-6 rounded-xl border-0 cursor-pointer disabled:opacity-70"
              style={{background: COLORS.white, color: COLORS.brand}}
            >
              <DownloadOutlined spin={exporting}/> Export
            </button>
          }
        />

        <Card className="mt-8 p-5">
          <Tabs
            activeKey={tab}
            onChange={(key) => setTab(key)}
            items={STATUS_TABS.map((entry) => ({
              key: entry.key,
              label: entry.label,
              // Only the active tab renders a table; each switch is one query, not eight.
              children: tab === entry.key ? table : null,
            }))}
            tabBarExtraContent={
              <Space size={4} className="text-xs" style={{color: COLORS.textMuted}}>
                {loading ? "Loading…" : `${pageInfo?.totalItems ?? 0} payments`}
              </Space>
            }
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default PaymentLinkReports;
